import React, { useState, useEffect, useMemo } from 'react';
import ConfirmDeleteDialog from '../../../components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import { clearAllNotifications, deleteNotification, getMyNotifications } from '../../../api/services/notificationService';
import { getNotificationSideForUserRole } from '../../../utils/notificationSide';
import { MdDeleteOutline, MdInbox } from 'react-icons/md';
import {
  getBookingIdFromNotification,
  getMoveOutAssignmentIdFromNotification,
  getUnitRequestIdFromNotification,
  getNotificationIconElement,
  isBnbBookingNotification,
  isBnbPaymentEvidenceNotification,
  isMoveOutRequestNotification,
  isUnitRequestNotification,
  isUnitRequestPaymentEvidenceNotification,
} from './notificationUtils';
import './Notifications.css';

const isAirbnbHostRole = (role) => role === 'Airbnb Host' || role === 'BNB Host';
const isLandlordOrAgentRole = (role) => role === 'Landlord' || role === 'Agent';

const Notifications = ({ onNavigate, userRole }) => {
  const notificationSide = useMemo(
    () => getNotificationSideForUserRole(userRole),
    [userRole]
  );

  const [allNotifications, setAllNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingIds, setDeletingIds] = useState(() => new Set());
  const [clearAllOpen, setClearAllOpen] = useState(false);
  const [clearAllLoading, setClearAllLoading] = useState(false);
  
  // Pagination State
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const backendPageRef = React.useRef(0);
  const backendHasMoreRef = React.useRef(true);
  const allNotificationsRef = React.useRef([]);
  
  const PAGE_SIZE = 20;

  const resetNotificationCache = () => {
    backendPageRef.current = 0;
    backendHasMoreRef.current = true;
    allNotificationsRef.current = [];
    setAllNotifications([]);
    setPage(0);
  };

  useEffect(() => {
    resetNotificationCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationSide]);

  useEffect(() => {
    ensureEnoughNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, notificationSide]);

  useEffect(() => {
    const handleNewNotification = () => {
      // Whenever a new foreground push notification is received, reset and instantly refetch!
      setTimeout(() => {
        backendPageRef.current = 0;
        backendHasMoreRef.current = true;
        allNotificationsRef.current = [];
        setAllNotifications([]);
        
        if (page === 0) {
          ensureEnoughNotifications();
        } else {
          setPage(0);
        }
      }, 800); // 800ms to allow database commits on the backend side
    };

    window.addEventListener('app:new_notification', handleNewNotification);
    return () => window.removeEventListener('app:new_notification', handleNewNotification);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const ensureEnoughNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let currentAll = allNotificationsRef.current;
      let currentBackendPage = backendPageRef.current;
      let hasMore = backendHasMoreRef.current;
      const targetNotifs = (page + 1) * PAGE_SIZE;

      while (currentAll.length < targetNotifs && hasMore) {
        const res = await getMyNotifications(currentBackendPage, 50, notificationSide);
        if (res && res.data) {
          const rawContent = res.data.content || [];
          
          // Client-side deduplication within the new batch
          const newDeduplicated = rawContent.filter((notif, index, self) => {
            return index === self.findIndex((n) => (
              n.type === notif.type &&
              n.title === notif.title &&
              n.body === notif.body &&
              Math.abs(new Date(n.createdAt).getTime() - new Date(notif.createdAt).getTime()) < 5000
            ));
          });
          
          // Ensure we don't add duplicates that already exist in our global list
          // eslint-disable-next-line no-loop-func
          const trulyNew = newDeduplicated.filter(newNotif => 
            !currentAll.some(existing => 
              existing.type === newNotif.type &&
              existing.title === newNotif.title &&
              existing.body === newNotif.body &&
              Math.abs(new Date(existing.createdAt).getTime() - new Date(newNotif.createdAt).getTime()) < 5000
            )
          );
          
          currentAll = [...currentAll, ...trulyNew];
          currentBackendPage++;
          
          const backendTotal = res.data.totalPages || 1;
          if (currentBackendPage >= backendTotal) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }
      
      allNotificationsRef.current = currentAll;
      backendPageRef.current = currentBackendPage;
      backendHasMoreRef.current = hasMore;
      
      setAllNotifications(currentAll);
      
      // Calculate dynamic total pages based on current accumulated size and whether backend has more
      const calculatedTotal = Math.ceil(currentAll.length / PAGE_SIZE);
      setTotalPages(hasMore ? Math.max(calculatedTotal + 1, page + 2) : Math.max(calculatedTotal, 1));
      
    } catch (err) {
      setError(err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const currentNotifications = allNotifications.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const removeNotificationFromList = (notificationId) => {
    const updated = allNotificationsRef.current.filter((n) => n.id !== notificationId);
    allNotificationsRef.current = updated;
    setAllNotifications(updated);

    const newTotal = Math.ceil(updated.length / PAGE_SIZE) || 1;
    setTotalPages(backendHasMoreRef.current ? Math.max(newTotal + 1, 1) : Math.max(newTotal, 1));

    const maxPage = Math.max(0, Math.ceil(updated.length / PAGE_SIZE) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }

    window.dispatchEvent(new CustomEvent('app:notification_deleted'));
  };

  const handleDeleteNotification = async (e, notif) => {
    e.stopPropagation();
    if (!notif?.id || deletingIds.has(notif.id)) return;

    setDeletingIds((prev) => new Set(prev).add(notif.id));
    try {
      await deleteNotification(notif.id);
      removeNotificationFromList(notif.id);
    } catch (err) {
      console.error('Failed to delete notification:', err);
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(notif.id);
        return next;
      });
    }
  };

  const handleClearAllConfirm = async () => {
    setClearAllLoading(true);
    try {
      await clearAllNotifications(notificationSide);
      resetNotificationCache();
      backendHasMoreRef.current = false;
      setTotalPages(1);
      window.dispatchEvent(new CustomEvent('app:notification_deleted'));
      setClearAllOpen(false);
    } catch (err) {
      console.error('Failed to clear all notifications:', err);
    } finally {
      setClearAllLoading(false);
    }
  };

  const handleNotificationClick = (notif) => {
    if (!onNavigate) return;

    if (isAirbnbHostRole(userRole) && isBnbBookingNotification(notif)) {
      const bookingId = getBookingIdFromNotification(notif);
      const isPayment = isBnbPaymentEvidenceNotification(notif);
      onNavigate('bookings', {
        bookingId: bookingId || undefined,
        notification: notif,
        focusBookingOnly: true,
        openPaymentEvidence: isPayment,
        bookingResolveHint: bookingId
          ? undefined
          : {
              createdAt: notif.createdAt,
            },
      });
      return;
    }

    if (isLandlordOrAgentRole(userRole) && isUnitRequestNotification(notif)) {
      const requestId = getUnitRequestIdFromNotification(notif);
      const isPayment = isUnitRequestPaymentEvidenceNotification(notif);
      onNavigate('unit-requests', {
        requestId: requestId || undefined,
        notification: notif,
        focusRequestOnly: true,
        openPaymentEvidence: isPayment,
        requestResolveHint: requestId
          ? undefined
          : {
              createdAt: notif.createdAt,
            },
      });
      return;
    }

    if (isLandlordOrAgentRole(userRole) && isMoveOutRequestNotification(notif)) {
      const assignmentId = getMoveOutAssignmentIdFromNotification(notif);
      onNavigate('move-out-requests', {
        assignmentId: assignmentId || undefined,
        notification: notif,
        focusMoveOutOnly: true,
        moveOutResolveHint: assignmentId
          ? undefined
          : {
              createdAt: notif.createdAt,
            },
      });
      return;
    }

    switch (notif.type) {
      case 'UNIT_REQUEST_SUBMITTED':
        onNavigate(userRole === 'Tenant' ? 'requests' : 'unit-requests');
        break;
      case 'TENANT_SCORE_ACCESS_APPROVED':
      case 'TENANT_SCORE_ACCESS_REJECTED':
        onNavigate(userRole === 'Tenant' ? 'score' : 'tenant-score');
        break;
      case 'ROLE_APPROVED':
        onNavigate('profile');
        break;
      case 'CHAT_MESSAGE':
      case 'NEW_MESSAGE':
        const meta = notif.metadata || {};
        const threadId = meta.thread_id || meta.threadId || notif.targetId || notif.target_id || notif.threadId || notif.thread_id;
        onNavigate('messages', { threadId });
        break;
      default:
        // Check title for 'message' as a fallback
        if (notif.title?.toLowerCase().includes('message')) {
          const meta = notif.metadata || {};
          const tId = meta.thread_id || meta.threadId || notif.targetId || notif.target_id || notif.threadId || notif.thread_id;
          onNavigate('messages', { threadId: tId });
        }
        break;
    }
  };

  const hasNotifications = allNotifications.length > 0;

  return (
    <div className="global-notifications-container">
      <ConfirmDeleteDialog
        open={clearAllOpen}
        title="Are you sure you want to clear all notifications?"
        message="All notifications for this dashboard will be permanently removed."
        description="This action cannot be undone."
        confirmLabel="Clear all"
        cancelLabel="Cancel"
        loading={clearAllLoading}
        onCancel={() => !clearAllLoading && setClearAllOpen(false)}
        onConfirm={handleClearAllConfirm}
      />

      <div className="notifications-content">
        {!loading && !error && hasNotifications && (
          <div className="notifications-toolbar">
            <button
              type="button"
              className="btn btn-secondary notifications-clear-all-btn"
              onClick={() => setClearAllOpen(true)}
            >
              Clear all notifications
            </button>
          </div>
        )}

        {loading ? (
          <div className="notifications-loading">
            <div className="spinner"></div>
            <p>Loading notifications...</p>
          </div>
        ) : error ? (
          <div className="notifications-error">
            <p>{error}</p>
            <button className="btn btn-primary" onClick={() => ensureEnoughNotifications()}>Retry</button>
          </div>
        ) : currentNotifications.length === 0 ? (
          <div className="notifications-empty">
            <div className="empty-icon notification-icon-wrapper">
              <MdInbox size={32} aria-hidden />
            </div>
            <h3>All Caught Up!</h3>
            <p>You have no notifications at the moment.</p>
          </div>
        ) : (
          <>
            <div className="notifications-list">
              {currentNotifications.map((notif) => (
                <div 
                  key={notif.id} 
                  className={`notification-card ${notif.readAt ? 'read' : 'unread'} clickable`}
                  onClick={() => handleNotificationClick(notif)}
                >
                  <div className={`notification-icon-wrapper ${notif.readAt ? '' : 'unread'}`}>
                    {getNotificationIconElement(notif.type, notif)}
                  </div>
                  <div className="notification-details">
                    <h4 className="notification-title">{notif.title}</h4>
                    <p className="notification-body">{notif.body}</p>
                    <span className="notification-date">{formatDate(notif.createdAt)}</span>
                  </div>
                  <button
                    type="button"
                    className="notification-delete-btn"
                    aria-label="Delete notification"
                    disabled={deletingIds.has(notif.id)}
                    onClick={(e) => handleDeleteNotification(e, notif)}
                  >
                    <MdDeleteOutline size={20} aria-hidden />
                  </button>
                </div>
              ))}
            </div>
            
            {totalPages > 1 && (
              <div className="notifications-pagination">
                <button 
                  className="btn btn-secondary pagination-btn" 
                  disabled={page === 0} 
                  onClick={() => setPage(page - 1)}
                >
                  ◀ Previous
                </button>
                <div className="pagination-indicators">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <span 
                      key={i} 
                      className={`page-dot ${i === page ? 'active' : ''}`}
                      onClick={() => setPage(i)}
                    />
                  ))}
                </div>
                <button 
                  className="btn btn-secondary pagination-btn" 
                  disabled={page === totalPages - 1} 
                  onClick={() => setPage(page + 1)}
                >
                  Next ▶
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Notifications;
